import { format } from 'date-fns';

import { dateTimeWordedPattern } from './format';
import { DATE_FORMATS, type DateInput, DISPLAY_DAY_FORMAT } from './formats';
import { appDateLocale } from './locales';
import { type ParseTsOptions, parseTs, toDate } from './parse';
import { guessTimeZone } from './timezone';
import { ZonedDate } from './zonedDate';

// What moment printed for a value it could not read; a bad value still shows as one.
const INVALID_DATE = 'Invalid date';

function formatParsed(
    ts: DateInput,
    formatString: string,
    opts?: ParseTsOptions
): string | null | undefined {
    const parsed = parseTs(ts, opts);
    if (parsed === null) return null;
    if (parsed === undefined) return undefined;
    const d = toDate(parsed);
    return d ? format(d, formatString, { locale: appDateLocale() }) : INVALID_DATE;
}

/**
 * The day of a timestamp as `yyyy-MM-dd`; null and undefined pass through. Numbers are
 * Unix seconds (see `parseTs`).
 */
export function dayToString(ts: Date | number, opts?: ParseTsOptions): string;
export function dayToString(ts: DateInput, opts?: ParseTsOptions): string | null | undefined;
export function dayToString(ts: DateInput, opts?: ParseTsOptions): string | null | undefined {
    return formatParsed(ts, DATE_FORMATS.internal, opts);
}

/**
 * The day of a timestamp for display, `dd/MM/yyyy` unless `opts.format` (date-fns tokens)
 * says otherwise; null and undefined pass through.
 */
export function formattedDay(ts: DateInput, opts?: { format?: string }): string | null | undefined {
    return formatParsed(ts, opts?.format ?? DISPLAY_DAY_FORMAT);
}

/**
 * A timestamp as the portal's worded display date-time to the second (see
 * `formatDateTimeWorded`), or in `opts.format`, in `timezone`, the browser's own zone by
 * default; null and undefined pass through.
 */
export function formattedTs(
    ts: DateInput,
    timezone: string = guessTimeZone(),
    opts?: { format?: string }
): string | null | undefined {
    const parsed = parseTs(ts);
    if (parsed === null) return null;
    if (parsed === undefined) return undefined;
    const d = toDate(parsed);
    if (!d) return INVALID_DATE;
    return format(
        new ZonedDate(d.getTime(), timezone),
        opts?.format ?? dateTimeWordedPattern('second'),
        {
            locale: appDateLocale(),
        }
    );
}
